import { inject, Injectable } from "@angular/core";
import { RdvSidePanelService } from "@featuresAY/rdv/rdv-side-panel.service";
import {
	SideNavActionsMenuColumn,
	SideNavActionsMenuPartItem,
} from "@core/layout/components/side-nav/components/side-nav-actions-menu/models/side-nav-actions-menu.model";
import { SIDE_NAV_ACTIONS_ITEMS } from "@core/layout/components/side-nav/components/side-nav-actions-menu/constants/side-nav-actions-items";
import { SideNavActionsIds } from "@core/layout/components/side-nav/components/side-nav-actions-menu/enums/side-nav-actions.ids";
import {
	AppState,
	OrdoFavoriSeanceFacade,
	PreferenceInterfaceOrdonnanceFacadeService,
	selectFullClient,
} from "features";
import { FullClient } from "@models/full-client.model";
import { NavigationService } from "@services/navigation.service";
import { AngularRoutesEnum, DojoRoutesEnum } from "@core/layout/enums/layout-routes.enum";
import { CvExistsModalAyComponent } from "@featuresAY/patient-web-ay/modals/cv-exists-modal-ay/cv-exists-modal-ay.component";
import { DialogSize, UIKitDialogConfig, UiKitDialogService, UIKitDialogType } from "@jack-sparrow-libraries/ui-kit";
import { InitActionLecteur } from "@featuresAY/lecteur/store/lecteur/lecteur.action";
import { buildCreationPatientReadCvActions } from "@featuresAY/lecteur/store/lecteur/lecteur-actions.factory";
import { v4 as uuidv4 } from "uuid";
import { Store } from "@ngrx/store";
import { PsSpecialite, SiteFonctionnalite } from "@jack-sparrow-libraries/emotion-models";
import { FonctionnaliteFacade } from "@shared-ay/fonctionnalite/fonctionnalite.facade";
import { EmotionEventsHelperService, StrutsActionType } from "@services/helpers/emotion-events-helper.service";
import { SynapseMonographComponent } from "@featuresAY/synapse-prescription-medicamenteuse/synapse-monograph/synapse-monograph.component";
import { OrdonnanceBsiChoixWebAyComponent } from "@featuresAY/ordonnance-web-ay/components/ordonnance-bsi-choix-web-ay/ordonnance-bsi-choix-web-ay.component";
import { firstValueFrom } from "rxjs";
import { InterfacePreferenceService, InterfaceVersion } from "@services/interface-preference.service";
import { CommonsCreationPatientService, CreationPatientSucess } from "@jack-sparrow-libraries/angular-business-commons";
import { CreationPatientService } from "@featuresAY/patient-web-ay/services/creation-patient.service";
import { NavigationFlag } from "@enums/navigation-flag.enum";
import { ConstantesEntryService } from "@featuresAY/constantes-bilan-panel/services/constantes-entry.service";

@Injectable({
	providedIn: "root",
})
export class SideNavActionsMenuService {
	private readonly navigationService = inject(NavigationService);
	private readonly dialogService = inject(UiKitDialogService);
	private readonly store = inject(Store<AppState>);
	private readonly fonctionnaliteFacade = inject(FonctionnaliteFacade);
	private readonly favoriSeanceFacade = inject(OrdoFavoriSeanceFacade);
	private readonly interfacePreferenceService = inject(InterfacePreferenceService);
	private readonly commonsCreationPatientService = inject(CommonsCreationPatientService);
	private readonly creationPatientService = inject(CreationPatientService);
	private readonly constantesEntry = inject(ConstantesEntryService);

	private readonly preferenceOrdonnanceFacade = inject(PreferenceInterfaceOrdonnanceFacadeService);
	private readonly rdvWebAySidePanelService = inject(RdvSidePanelService);

	private readonly actionsMap: Partial<Record<SideNavActionsIds, () => void | Promise<void>>> = {
		[SideNavActionsIds.ORDONNANCE_RAPIDE]: () => this.goToOrdonnanceRapide(),
		[SideNavActionsIds.ORDONNANCE_BSI]: () => this.goToOrdonnanceBsi(),
		[SideNavActionsIds.ORDONNANCE_MONOGRAPHIE]: () => this.goToMonographie(),
		[SideNavActionsIds.PAIEMENT_REDEVANCE]: () => this.openRedevanceDialog(),
		[SideNavActionsIds.PAIEMENT_CHIFFRE_AFFAIRES]: () => this.openChiffreAffairesDialog(),
		[SideNavActionsIds.PATIENTELE_PATIENT]: () => this.createPatient(),
		[SideNavActionsIds.PATIENTELE_RENDEZ_VOUS]: () => this.rdvWebAySidePanelService.openCreation(),
		[SideNavActionsIds.PATIENTELE_LECTURE_CV]: () => this.readCv(),
		[SideNavActionsIds.COORDINATION_CONSTANTE]: () => this.constantesEntry.openCreate(),
	};

	generateItems(fullClient: FullClient): SideNavActionsMenuColumn[] {
		return SIDE_NAV_ACTIONS_ITEMS.map((column) => ({
			...column,
			items: column.items.filter((item) => this.filterItems(item, fullClient)).map((item) => this.mapItem(item)),
		}));
	}

	private filterItems(item: SideNavActionsMenuPartItem, fullClient: FullClient): boolean {
		switch (item.id) {
			case SideNavActionsIds.ORDONNANCE_PRESCRIPTION_IPA:
			case SideNavActionsIds.ORDONNANCE_MONOGRAPHIE:
				if (!this.fonctionnaliteFacade.hasAnyFonctionnalite(SiteFonctionnalite.PrescriptionMedicamenteuse)()) {
					return false;
				}
				break;

			case SideNavActionsIds.ORDONNANCE_PRESCRIPTION_IDEL:
				if (fullClient.codeSpecialite === PsSpecialite.INFIRMIER_PRATIQUE_AVANCEE) {
					return false;
				}

				break;

			default:
				return true;
		}

		return true;
	}

	private mapItem(item: SideNavActionsMenuPartItem) {
		return {
			...item,
			action: this.actionsMap[item.id] || null,
		};
	}

	private async goToOrdonnanceRapide() {
		const fullClient = await firstValueFrom(this.store.select(selectFullClient));
		if (
			this.preferenceOrdonnanceFacade.isV2() &&
			fullClient?.codeSpecialite === PsSpecialite.INFIRMIER_PRATIQUE_AVANCEE
		) {
			await this.favoriSeanceFacade.loadAllFavoriSeances();
			this.navigationService.navAngularV2([
				AngularRoutesEnum.ORDONNANCE_WEB_AY,
				AngularRoutesEnum.ORDO_RAPIDE_IPA_SELECT,
			]);
		} else {
			this.navigationService.navDojo([DojoRoutesEnum.ORDONNANCES, DojoRoutesEnum.CHOIX_PATIENT], {
				queryParams: {
					ordoType: "rapide",
				},
			});
		}
	}

	private goToOrdonnanceBsi() {
		if (this.preferenceOrdonnanceFacade.isV1()) {
			this.navigationService.navDojo([DojoRoutesEnum.ORDONNANCES, DojoRoutesEnum.CHOIX_PATIENT], {
				queryParams: {
					ordoType: "bsi",
				},
			});
		} else {
			this.dialogService.openDialog(OrdonnanceBsiChoixWebAyComponent, {
				closeButton: false,
				noPadding: true,
			});
		}
	}

	goToMonographie() {
		const config: UIKitDialogConfig = {
			type: UIKitDialogType.SidePanel,
			size: DialogSize.large,
			closeButton: true,
			disableOutside: false,
		};
		this.dialogService.openDialog(SynapseMonographComponent, config);
	}

	private openRedevanceDialog() {
		EmotionEventsHelperService.dispatchEvent({
			action: StrutsActionType.ON_MENU_ACTIONS_ADD,
			payload: {
				id: SideNavActionsIds.PAIEMENT_REDEVANCE,
				params: {
					action: "redevance",
				},
				samePage: true,
			},
		});
	}

	private openChiffreAffairesDialog() {
		EmotionEventsHelperService.dispatchEvent({
			action: StrutsActionType.ON_MENU_ACTIONS_ADD,
			payload: {
				id: SideNavActionsIds.PAIEMENT_CHIFFRE_AFFAIRES,
				params: {
					action: "ca",
				},
				samePage: true,
			},
		});
	}

	private async createPatient() {
		if ((await this.interfacePreferenceService.getPatientVersion()) === InterfaceVersion.V2) {
			const retourCreation: CreationPatientSucess = await this.commonsCreationPatientService.start();
			this.creationPatientService.syncAfterCreationV2(retourCreation);
			if (retourCreation?.navigation) {
				void this.creationPatientService.routeAfterCreatePatient(
					retourCreation.navigation,
					retourCreation.patient?.id,
				);
			}
		} else {
			// Ce menu est accessible depuis n'importe quelle page, dont l'accueil Struts : on la marque
			// pour que « Terminer » y revienne, au lieu de retomber sur la liste des patients.
			this.navigationService.flagPage(NavigationFlag.CreationPatient);
			this.dialogService.openDialog(CvExistsModalAyComponent, {
				noPadding: true,
				closeButton: false,
			});
		}
	}

	private readCv() {
		this.store.dispatch(
			new InitActionLecteur({
				id: uuidv4(),
				// AY-10282 : batch mutualisé — vérif préventive CPS AVANT lecture Vitale (même code porteur).
				actions: buildCreationPatientReadCvActions(),
				needCodePorteur: true,
				options: {
					loader: { title: "Connexion à CBA link" },
					onSuccess: () => {
						this.navigationService.navAngularV2([
							AngularRoutesEnum.PATIENT_WEB_AY,
							AngularRoutesEnum.CREATION_PATIENT_LIST_BENEFICIAIRES,
						]);
					},
				},
			}),
		);
	}
}
